/**
 * Dashboard — Targets widget.
 *
 * Full-width card showing the agency target for the selected period
 * (Monthly / Quarterly / Annual), with a pace badge and four sub-target
 * tiles (Trips · New Clients · Leads Closed · Invoiced ₹).
 *
 * Backed by /api/targets (see `backend/routes/targets.py`). When no target
 * has been set for the period an inline CTA collects a single revenue
 * figure and creates the target on the spot.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Target as TargetIcon, Pencil } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRIMARY = '#0BA888';

// Indian-FY-aware period helpers. Defaults match the rest of the app
// (Apr→Mar FY, Q1 = Apr-Jun).
const _today = () => new Date();
const _currentMonthly = () => {
  const d = _today();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const _currentQuarterly = () => {
  const d = _today();
  const m = d.getMonth() + 1;
  // Q1 Apr-Jun, Q2 Jul-Sep, Q3 Oct-Dec, Q4 Jan-Mar
  let q, sy;
  if (m >= 4 && m <= 6) { q = 1; sy = d.getFullYear(); }
  else if (m >= 7 && m <= 9) { q = 2; sy = d.getFullYear(); }
  else if (m >= 10 && m <= 12) { q = 3; sy = d.getFullYear(); }
  else { q = 4; sy = d.getFullYear() - 1; }
  return `Q${q}-${sy}`;
};
const _currentAnnual = () => {
  const d = _today();
  const sy = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `FY-${sy}`;
};

const fmtINR = (n) => `₹${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;
const fmtINRCompact = (n) => {
  const v = Number(n || 0);
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
  return `₹${Math.round(v)}`;
};

const TargetsWidget = () => {
  const [periodType, setPeriodType] = useState('monthly');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [inlineRevenue, setInlineRevenue] = useState('');
  const [busy, setBusy] = useState(false);

  const periodValue = (
    periodType === 'monthly' ? _currentMonthly()
    : periodType === 'quarterly' ? _currentQuarterly()
    : _currentAnnual()
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/targets?period_type=${periodType}&period_value=${periodValue}`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load targets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [periodType]);

  const saveInline = async () => {
    const v = Number(inlineRevenue);
    if (!v || v <= 0) { toast.error('Enter a revenue target'); return; }
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/targets`, {
        period_type: periodType,
        period_value: periodValue,
        revenue_target: v,
        trips_target: 0,
        new_clients_target: 0,
        leads_close_target: 0,
      }, { withCredentials: true });
      toast.success('Target set');
      setInlineRevenue('');
      fetchData();
    } catch (err) {
      toast.error('Failed to save target');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !data) {
    return (
      <Card className="p-5 border-slate-200" data-testid="targets-widget-loading">
        <div className="text-sm text-slate-400">Loading targets…</div>
      </Card>
    );
  }

  return (
    <Card className="p-5 border-slate-200" data-testid="targets-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TargetIcon className="h-5 w-5 text-emerald-600" />
          <h3 className="text-base font-semibold text-slate-900">Targets</h3>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex bg-slate-100 rounded-lg p-0.5" data-testid="targets-period-toggle">
            {['monthly', 'quarterly', 'annual'].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriodType(p)}
                data-testid={`targets-period-${p}`}
                className={`px-3 py-1 text-xs font-semibold rounded-md capitalize transition-colors ${
                  periodType === p ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
          {data.target ? (
            <Button variant="ghost" size="sm" onClick={() => setModalOpen(true)} data-testid="edit-targets-btn">
              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
            </Button>
          ) : null}
        </div>
      </div>

      {data.target ? <TargetBody data={data} /> : <InlineCTA value={inlineRevenue} onChange={setInlineRevenue} onSave={saveInline} busy={busy} />}

      <TargetsEditModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        target={data.target}
        periodType={periodType}
        periodValue={periodValue}
        onSaved={() => { setModalOpen(false); fetchData(); }}
      />
    </Card>
  );
};

const TargetBody = ({ data }) => {
  const target = data.target;
  const achieved = data.achieved;
  const period = data.period;
  const pace = data.pace;
  const revPct = target.revenue_target > 0 ? Math.min(100, (achieved.revenue / target.revenue_target) * 100) : 0;
  const paceLabel = pace.label || 'on_track';
  const paceColor = paceLabel === 'on_track' ? '#0BA888' : paceLabel === 'behind' ? '#D97706' : '#DC2626';
  const paceText = paceLabel === 'on_track' ? 'On track' : paceLabel === 'behind' ? 'Behind pace' : 'Critical';

  return (
    <>
      <div className="flex items-baseline gap-3 flex-wrap mb-3">
        <span className="font-mono font-semibold" style={{ fontSize: 28, color: '#0BA888' }}>{fmtINR(achieved.revenue)}</span>
        <span className="text-xs text-slate-500">of</span>
        <span className="font-mono font-semibold text-slate-900" style={{ fontSize: 20 }}>{fmtINR(target.revenue_target)}</span>
        {pace.label ? (
          <span
            data-testid="pace-badge"
            className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full"
            style={{ backgroundColor: paceColor + '20', color: paceColor }}
          >
            {paceText}
          </span>
        ) : null}
      </div>

      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${revPct}%`, backgroundColor: paceColor }} data-testid="target-progress-bar" />
      </div>

      <div className="text-[11px] text-slate-500 mt-2 font-mono flex items-center gap-3 flex-wrap" data-testid="pace-strip">
        <span>{period.days_remaining} days left</span>
        {pace.projected_revenue !== null && pace.projected_revenue !== undefined ? (
          <>
            <span>·</span>
            <span>Projected {fmtINRCompact(pace.projected_revenue)}</span>
          </>
        ) : null}
        {pace.gap !== null && pace.gap !== undefined ? (
          <>
            <span>·</span>
            <span className={pace.gap > 0 ? 'text-rose-600' : 'text-emerald-600'}>
              Gap {pace.gap > 0 ? '+' : ''}{fmtINRCompact(pace.gap)}
            </span>
          </>
        ) : null}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-slate-100 pt-4 mt-4">
        <SubTile label="Trips" achieved={achieved.trips} target={target.trips_target} format={(v) => String(v)} testid="sub-trips" />
        <SubTile label="New clients" achieved={achieved.new_clients} target={target.new_clients_target} format={(v) => String(v)} testid="sub-clients" />
        <SubTile label="Leads closed" achieved={achieved.leads_closed} target={target.leads_close_target} format={(v) => String(v)} testid="sub-leads" />
        <SubTile label="Invoiced" achieved={achieved.revenue} target={target.revenue_target} format={fmtINRCompact} testid="sub-revenue" />
      </div>
    </>
  );
};

const SubTile = ({ label, achieved, target, format, testid }) => {
  const pct = target > 0 ? Math.min(100, (achieved / target) * 100) : 0;
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-sm font-semibold text-slate-900 mt-0.5">
        {format(achieved)} <span className="text-slate-400 font-normal">/ {target > 0 ? format(target) : '—'}</span>
      </div>
      <div className="h-1 bg-slate-100 rounded-full mt-1.5 overflow-hidden">
        <div className="h-full" style={{ width: `${pct}%`, backgroundColor: PRIMARY }} />
      </div>
    </div>
  );
};

const InlineCTA = ({ value, onChange, onSave, busy }) => (
  <div className="flex items-center gap-2 flex-wrap" data-testid="targets-inline-cta">
    <span className="text-sm text-slate-600 mr-2">No target set. Set revenue target:</span>
    <Input
      type="number"
      placeholder="₹500000"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-40"
      data-testid="inline-target-input"
    />
    <Button onClick={onSave} disabled={busy} data-testid="inline-target-save-btn" className="bg-emerald-700 hover:bg-emerald-800 text-white">
      {busy ? 'Saving…' : 'Save'}
    </Button>
  </div>
);

const TargetsEditModal = ({ open, onOpenChange, target, periodType, periodValue, onSaved }) => {
  const [revenue, setRevenue] = useState('');
  const [trips, setTrips] = useState('');
  const [clients, setClients] = useState('');
  const [leads, setLeads] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target) {
      setRevenue(target.revenue_target || '');
      setTrips(target.trips_target || '');
      setClients(target.new_clients_target || '');
      setLeads(target.leads_close_target || '');
    }
  }, [target, open]);

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/targets`, {
        period_type: periodType,
        period_value: periodValue,
        revenue_target: Number(revenue) || 0,
        trips_target: Number(trips) || 0,
        new_clients_target: Number(clients) || 0,
        leads_close_target: Number(leads) || 0,
      }, { withCredentials: true });
      toast.success('Targets updated');
      onSaved();
    } catch {
      toast.error('Failed to save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-targets-dialog">
        <DialogHeader>
          <DialogTitle>Edit targets — {periodValue}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label>Revenue target (₹)</Label>
            <Input type="number" value={revenue} onChange={(e) => setRevenue(e.target.value)} data-testid="target-revenue-input" />
          </div>
          <div>
            <Label>Trips target</Label>
            <Input type="number" value={trips} onChange={(e) => setTrips(e.target.value)} data-testid="target-trips-input" />
          </div>
          <div>
            <Label>New clients target</Label>
            <Input type="number" value={clients} onChange={(e) => setClients(e.target.value)} data-testid="target-clients-input" />
          </div>
          <div>
            <Label>Leads closed target</Label>
            <Input type="number" value={leads} onChange={(e) => setLeads(e.target.value)} data-testid="target-leads-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-targets-btn">
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TargetsWidget;
